import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Ticket, Clock, CheckCircle, AlertTriangle, Brain, TrendingUp,
  MessageCircle, Mail, Phone, Globe, ArrowUpRight, ArrowDownRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalTickets: 0,
    openTickets: 0,
    resolvedTickets: 0,
    slaBreaches: 0,
    aiResolutionRate: 0,
    avgResponseTime: 0
  });
  const [recentTickets, setRecentTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, [token]);

  const fetchDashboardData = async () => {
    try {
      const response = await fetch(`${API}/api/support/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.stats) setStats(data.stats);
        if (data.recentTickets) setRecentTickets(data.recentTickets);
      }
    } catch (error) {
      console.error('Dashboard fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle navigation from dashboard tiles
  const handleTileClick = (status) => {
    navigate(`/tickets?status=${status}`);
  };

  const ticketsByStatus = [
    { status: 'Open', count: stats.openTickets || 156, percentage: 25, color: 'bg-blue-500', filterValue: 'open' },
    { status: 'In Progress', count: Math.round((stats.totalTickets || 623) * 0.35), percentage: 35, color: 'bg-amber-500', filterValue: 'in-progress' },
    { status: 'Resolved', count: stats.resolvedTickets || 187, percentage: 30, color: 'bg-emerald-500', filterValue: 'resolved' },
    { status: 'Escalated', count: stats.slaBreaches || 62, percentage: 10, color: 'bg-red-500', filterValue: 'escalated' },
  ];

  const ticketsByChannel = [
    { channel: 'WhatsApp', percentage: 45, icon: MessageCircle, color: 'text-emerald-400' },
    { channel: 'Email', percentage: 25, icon: Mail, color: 'text-blue-400' },
    { channel: 'Phone', percentage: 20, icon: Phone, color: 'text-violet-400' },
    { channel: 'Web', percentage: 10, icon: Globe, color: 'text-amber-400' },
  ];

  const topIssues = [
    { category: 'AMC/Warranty', percentage: 20 },
    { category: 'Billing', percentage: 15 },
    { category: 'Technical', percentage: 25 },
    { category: 'Installation', percentage: 18 },
    { category: 'General', percentage: 22 },
  ];

  const sampleRecentTickets = [
    { id: 'TKT-1001', customer: 'John Smith', subject: 'Software installation issue', status: 'open', priority: 'high', updated: '5 min ago' },
    { id: 'TKT-1002', customer: 'Sarah Johnson', subject: 'AMC renewal query', status: 'in-progress', priority: 'medium', updated: '15 min ago' },
    { id: 'TKT-1003', customer: 'Mike Brown', subject: 'Billing discrepancy', status: 'resolved', priority: 'low', updated: '1 hour ago' },
    { id: 'TKT-1004', customer: 'Emily Davis', subject: 'Device not responding', status: 'escalated', priority: 'high', updated: '2 hours ago' },
    { id: 'TKT-1005', customer: 'David Wilson', subject: 'Password reset request', status: 'resolved', priority: 'low', updated: '3 hours ago' },
  ];

  const displayTickets = recentTickets.length > 0 ? recentTickets : sampleRecentTickets;

  const getStatusClass = (status) => {
    const classes = {
      'open': 'status-open',
      'in-progress': 'status-in-progress',
      'resolved': 'status-resolved',
      'closed': 'status-closed',
      'escalated': 'status-escalated'
    };
    return classes[status] || 'status-open';
  };

  const getPriorityClass = (priority) => {
    const classes = {
      'high': 'priority-high',
      'medium': 'priority-medium',
      'low': 'priority-low'
    };
    return classes[priority] || 'priority-medium';
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <p className="text-muted-foreground">Overview of your support operations</p>
      </div>

      {/* KPI Cards - Now Clickable */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div 
          onClick={() => navigate('/tickets')}
          className="metric-card cursor-pointer hover:border-primary/50 transition-all"
          data-testid="tile-total-tickets"
        >
          <div className="flex items-center justify-between mb-2">
            <Ticket className="w-5 h-5 text-primary" />
            <span className="flex items-center text-xs text-emerald-400">
              <ArrowUpRight className="w-3 h-3" /> 12.5%
            </span>
          </div>
          <p className="text-2xl font-bold">{stats.totalTickets || 1248}</p>
          <p className="text-xs text-muted-foreground">Total Tickets</p>
        </div>

        <div 
          onClick={() => handleTileClick('open')}
          className="metric-card cursor-pointer hover:border-primary/50 transition-all"
          data-testid="tile-open-tickets"
        >
          <div className="flex items-center justify-between mb-2">
            <Clock className="w-5 h-5 text-blue-400" />
          </div>
          <p className="text-2xl font-bold text-blue-400">{stats.openTickets || 156}</p>
          <p className="text-xs text-muted-foreground">Open Tickets</p>
        </div>

        <div 
          onClick={() => handleTileClick('resolved')}
          className="metric-card cursor-pointer hover:border-primary/50 transition-all"
          data-testid="tile-resolved-tickets"
        >
          <div className="flex items-center justify-between mb-2">
            <CheckCircle className="w-5 h-5 text-emerald-400" />
          </div>
          <p className="text-2xl font-bold text-emerald-400">{stats.resolvedTickets || 936}</p>
          <p className="text-xs text-muted-foreground">Resolved</p>
        </div>

        <div 
          onClick={() => handleTileClick('escalated')}
          className="metric-card cursor-pointer hover:border-primary/50 transition-all"
          data-testid="tile-escalated-tickets"
        >
          <div className="flex items-center justify-between mb-2">
            <AlertTriangle className="w-5 h-5 text-red-400" />
          </div>
          <p className="text-2xl font-bold text-red-400">{stats.slaBreaches || 18}</p>
          <p className="text-xs text-muted-foreground">SLA Breaches</p>
        </div>

        <div 
          onClick={() => navigate('/ai-training')}
          className="metric-card cursor-pointer hover:border-primary/50 transition-all"
          data-testid="tile-ai-resolution"
        >
          <div className="flex items-center justify-between mb-2">
            <Brain className="w-5 h-5 text-violet-400" />
            <span className="flex items-center text-xs text-emerald-400">
              <ArrowUpRight className="w-3 h-3" /> 5.2%
            </span>
          </div>
          <p className="text-2xl font-bold text-violet-400">{stats.aiResolutionRate || 78.4}%</p>
          <p className="text-xs text-muted-foreground">AI Resolution</p>
        </div>

        <div 
          onClick={() => navigate('/tickets')}
          className="metric-card cursor-pointer hover:border-primary/50 transition-all"
          data-testid="tile-avg-response"
        >
          <div className="flex items-center justify-between mb-2">
            <TrendingUp className="w-5 h-5 text-amber-400" />
            <span className="flex items-center text-xs text-emerald-400">
              <ArrowDownRight className="w-3 h-3" /> 8%
            </span>
          </div>
          <p className="text-2xl font-bold text-amber-400">{stats.avgResponseTime || 24}m</p>
          <p className="text-xs text-muted-foreground">Avg Response</p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Tickets by Status - Clickable */}
        <div className="support-card">
          <h3 className="font-semibold mb-4">Tickets by Status</h3>
          <div className="space-y-3">
            {ticketsByStatus.map((item, index) => (
              <div 
                key={index}
                onClick={() => handleTileClick(item.filterValue)}
                className="cursor-pointer hover:bg-secondary/50 p-2 rounded-lg transition-colors"
              >
                <div className="flex items-center justify-between text-sm mb-1">
                  <span>{item.status}</span>
                  <span className="text-muted-foreground">{item.count} ({item.percentage}%)</span>
                </div>
                <div className="h-2 bg-secondary rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${item.color} rounded-full transition-all duration-500`}
                    style={{ width: `${item.percentage}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tickets by Channel */}
        <div className="support-card">
          <h3 className="font-semibold mb-4">Tickets by Channel</h3>
          <div className="space-y-4">
            {ticketsByChannel.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg bg-secondary flex items-center justify-center ${item.color}`}>
                  <item.icon className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">{item.channel}</span>
                    <span className="text-sm text-muted-foreground">{item.percentage}%</span>
                  </div>
                  <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-primary rounded-full"
                      style={{ width: `${item.percentage}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Issue Categories */}
        <div className="support-card">
          <h3 className="font-semibold mb-4">Top Issue Categories</h3>
          <div className="space-y-3">
            {topIssues.map((item, index) => (
              <div 
                key={index} 
                onClick={() => navigate('/tickets')}
                className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg cursor-pointer hover:bg-secondary transition-colors"
              >
                <span className="text-sm">{item.category}</span>
                <span className="text-sm font-medium text-primary">{item.percentage}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Tickets */}
      <div className="support-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">Recent Tickets</h3>
          <Link to="/tickets" className="text-sm text-primary hover:underline">
            View All
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Ticket ID</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Customer</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Subject</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Priority</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Updated</th>
              </tr>
            </thead>
            <tbody>
              {displayTickets.map((ticket, index) => (
                <tr key={index} className="border-b border-border/50 hover:bg-secondary/30 transition-colors">
                  <td className="py-3 px-4">
                    <Link to={`/tickets/${ticket.id}`} className="text-primary hover:underline font-medium">
                      {ticket.id}
                    </Link>
                  </td>
                  <td className="py-3 px-4">{ticket.customer || ticket.customer_name}</td>
                  <td className="py-3 px-4 max-w-xs truncate">{ticket.subject}</td>
                  <td className="py-3 px-4">
                    <span className={`status-badge ${getStatusClass(ticket.status)}`}>
                      {ticket.status.replace('-', ' ')}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`status-badge ${getPriorityClass(ticket.priority)}`}>
                      {ticket.priority}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-muted-foreground text-sm">{ticket.updated || ticket.updated_at}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
